import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthData } from '../../core/declarations/auth-data.interface';
import { STORAGE_KEYS } from '../../core/declarations/storage-keys.enum';
import { Preferences } from '@capacitor/preferences';

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  // In-memory cache of the freshest token we've seen, used to gate writes
  // synchronously. Avoids a Preferences.get round-trip on the hot path,
  // which delayed the post-login Preferences.set enough that the very next
  // request (getCurrentUser) read empty AUTH and got 401 → forced logout.
  private lastAccepted: { client: string; expiry: number } | null = null;

  // Freshest full auth payload; requests read this instead of racing the
  // still-pending Preferences write right after login.
  private latestAuthData: AuthData | null = null;

  // Serializes Preferences.set calls so two concurrent writes can't race
  // in the underlying SharedPreferences layer on Android.
  private writeQueue: Promise<void> = Promise.resolve();

  constructor() {}

  public async getFreshAuthData(): Promise<AuthData | null> {
    if (this.latestAuthData) return this.latestAuthData;

    await this.writeQueue;
    const stored = await Preferences.get({ key: STORAGE_KEYS.AUTH });
    if (!stored.value) return null;

    const authData = JSON.parse(stored.value) as AuthData;
    this.seedFromStorage(authData);
    return authData;
  }

  // On cold start lastAccepted is empty while storage still holds the live
  // session — without seeding it, mayAcceptClient drops the server's rotated
  // tokens on regular endpoints and the session dies within the batch window.
  private seedFromStorage(authData: AuthData): void {
    if (this.lastAccepted || !authData?.client) return;

    const expiry = Number.parseInt(authData.expiry, 10);
    this.lastAccepted = {
      client: authData.client,
      expiry: Number.isFinite(expiry) ? expiry : 0
    };
  }

  public clear(): void {
    this.latestAuthData = null;
    this.lastAccepted = null;
  }

  // Only these endpoints may establish a session for a different client/uid;
  // anywhere else a foreign token is a late response from a previous session
  // and accepting it would hijack the fresh login (user saw the previous
  // account's data after logout + login).
  private static readonly SESSION_ESTABLISHING_URLS = [
    '/auth/sign_in',
    '/auth/validate_token',
    '/auth/invitation',
    '/auth/password'
  ];

  public getAuthHeadersFromResponse(
    data: HttpResponse<unknown> | HttpErrorResponse
  ): void {
    const headers = data.headers;

    const authData: AuthData = {
      accessToken: headers.get('access-token'),
      client: headers.get('client'),
      expiry: headers.get('expiry'),
      tokenType: headers.get('token-type'),
      uid: headers.get('uid')
    };

    if (!this.hasCompleteAuthData(authData)) return;
    if (!this.mayAcceptClient(authData, data.url)) return;
    if (!this.isAtLeastAsFresh(authData)) return;

    // Update in-memory state synchronously so a later response in the same
    // tick is compared against this one even before Preferences.set resolves.
    const incomingExpiry = Number.parseInt(authData.expiry, 10);
    this.lastAccepted = {
      client: authData.client,
      expiry: Number.isFinite(incomingExpiry) ? incomingExpiry : 0
    };
    this.latestAuthData = authData;

    this.writeQueue = this.writeQueue.then(() =>
      Preferences.set({
        key: STORAGE_KEYS.AUTH,
        value: JSON.stringify(authData)
      }).then(() => undefined)
    );
  }

  private mayAcceptClient(incoming: AuthData, url: string | null): boolean {
    if (this.lastAccepted?.client === incoming.client) return true;

    return TokenService.SESSION_ESTABLISHING_URLS.some(endpoint =>
      (url ?? '').includes(endpoint)
    );
  }

  private isAtLeastAsFresh(incoming: AuthData): boolean {
    // A new client (fresh login) starts its own freshness lineage. Same
    // client only wins if its expiry is >= what we last kept.
    if (!this.lastAccepted || this.lastAccepted.client !== incoming.client) {
      return true;
    }
    const incomingExpiry = Number.parseInt(incoming.expiry, 10);
    if (!Number.isFinite(incomingExpiry)) return true;
    return incomingExpiry >= this.lastAccepted.expiry;
  }

  private hasCompleteAuthData(authData: AuthData): boolean {
    // Filters out response headers on requests that aren't protected so empty
    // values are not stored.
    return !!(
      authData &&
      authData.accessToken &&
      authData.client &&
      authData.expiry &&
      authData.tokenType &&
      authData.uid
    );
  }
}
